import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { Schedule as ScheduleIcon, Stadium as StadiumIcon } from '@mui/icons-material';
import { COLORS } from './MAMCStyles';
import { styled } from '@mui/material/styles';

// Styled table components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: COLORS.background.light,
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  border: `1px solid rgba(218,165,32,0.1)`,
  overflow: 'hidden',
}));

const StyledTableHead = styled(TableHead)(() => ({
  background: COLORS.gradient.gold,
  '& .MuiTableCell-head': {
    color: COLORS.background.dark,
    fontWeight: 'bold',
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '16px 12px',
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  '&:hover': {
    backgroundColor: 'rgba(218,165,32,0.1)',
    transform: 'scale(1.01)',
    transition: 'all 0.2s ease',
  },
  '& .MuiTableCell-root': {
    borderBottom: `1px solid rgba(255,255,255,0.1)`,
    color: COLORS.text.primary,
    padding: '12px',
    fontSize: '0.875rem',
  },
}));

const StyledTableCell = styled(TableCell)(() => ({
  color: COLORS.text.primary,
  '&.team-cell': {
    fontWeight: '500',
    color: COLORS.text.primary,
  },
  '&.time-cell': {
    color: COLORS.text.secondary,
    fontFamily: 'monospace',
  },
  '&.field-cell': {
    color: COLORS.text.accent,
    fontWeight: '500',
  },
}));

// Styled card for other matches
const StyledMatchCard = styled(Card)(({ theme }) => ({
  backgroundColor: COLORS.background.light,
  color: COLORS.text.primary,
  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
  borderRadius: theme.spacing(1.5),
  border: `1px solid rgba(218,165,32,0.1)`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 24px rgba(218,165,32,0.3)',
    border: `1px solid rgba(218,165,32,0.3)`,
  },
}));

const UpcomingMatches = ({ matches, teams }) => {
  // Filter upcoming group stage matches (not finished, not ongoing)
  const upcomingGroupMatches = matches
    .filter(
      match => !match.isFinished && !match.ongoing && (!match.type || match.type === 'Group stage')
    )
    .sort((a, b) => new Date(a.time) - new Date(b.time));

  // Filter other upcoming matches (not group stage, not semi/final)
  const otherUpcomingMatches = matches
    .filter(
      match =>
        !match.isFinished &&
        !match.ongoing &&
        match.type &&
        match.type !== 'Group stage' &&
        match.type !== 'Semi Final' &&
        match.type !== 'Final'
    )
    .sort((a, b) => new Date(a.time) - new Date(b.time));

  if (upcomingGroupMatches.length === 0 && otherUpcomingMatches.length === 0) {
    return null;
  }

  const formatMatchTime = timeString => {
    const date = new Date(timeString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const timeFormat = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    if (isToday) {
      return `Today ${timeFormat}`;
    } else if (isTomorrow) {
      return `Tomorrow ${timeFormat}`;
    } else {
      const dateFormat = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      return `${dateFormat} ${timeFormat}`;
    }
  };

  return (
    <Box
      sx={{
        mb: { xs: 2, sm: 3, md: 4 },
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${COLORS.background.light}`,
        }}
      >
        <ScheduleIcon
          sx={{
            color: COLORS.secondary,
            fontSize: { xs: 24, sm: 28 },
            filter: 'drop-shadow(0 2px 4px rgba(218,165,32,0.3))',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.accent,
            fontSize: { xs: '1rem', sm: '1.25rem' },
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          Upcoming Matches
        </Typography>
      </Box>

      {/* Group Stage Matches Table */}
      {upcomingGroupMatches.length > 0 && (
        <>
          <Typography
            variant="subtitle1"
            sx={{
              color: COLORS.text.primary,
              fontSize: '1rem',
              fontWeight: 'bold',
              mb: 2,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Group Stage Matches
          </Typography>
          <Box sx={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
            <StyledTableContainer component={Paper} sx={{ mb: 3, overflow: 'visible' }}>
              <Table size="small" sx={{ minWidth: { xs: 600, sm: 700 } }}>
                <StyledTableHead>
                  <TableRow>
                    <TableCell>Match</TableCell>
                    <TableCell align="center">Group</TableCell>
                    <TableCell align="center">Time</TableCell>
                    <TableCell align="center">Field</TableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {upcomingGroupMatches.map(match => {
                    const homeTeam = teams.find(t => t.id === match.home_team_id);
                    const awayTeam = teams.find(t => t.id === match.away_team_id);

                    return (
                      <StyledTableRow key={match.id}>
                        <StyledTableCell className="team-cell">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: '500',
                                color: COLORS.text.primary,
                              }}
                            >
                              {homeTeam?.team_name || 'TBD'}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: COLORS.text.secondary,
                                mx: 1,
                              }}
                            >
                              vs
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: '500',
                                color: COLORS.text.primary,
                              }}
                            >
                              {awayTeam?.team_name || 'TBD'}
                            </Typography>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              color: COLORS.text.accent,
                              fontSize: '0.875rem',
                            }}
                          >
                            {homeTeam?.group || 'N/A'}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center" className="time-cell">
                          {formatMatchTime(match.time)}
                        </StyledTableCell>
                        <StyledTableCell align="center" className="field-cell">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                            }}
                          >
                            <StadiumIcon sx={{ fontSize: 16, color: COLORS.text.accent }} />
                            {match.field}
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Box>
        </>
      )}

      {/* Other Matches Tiles */}
      {otherUpcomingMatches.length > 0 && (
        <>
          <Typography
            variant="subtitle1"
            sx={{
              color: COLORS.text.primary,
              fontSize: '1rem',
              fontWeight: 'bold',
              mb: 2,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Other Upcoming Matches
          </Typography>
          <Grid container spacing={2}>
            {otherUpcomingMatches.map(match => {
              const homeTeam = teams.find(t => t.id === match.home_team_id);
              const awayTeam = teams.find(t => t.id === match.away_team_id);

              return (
                <Grid item xs={12} sm={6} md={4} key={match.id}>
                  <StyledMatchCard>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Match Type Badge */}
                      <Chip
                        label={match.type}
                        size="small"
                        sx={{
                          background: COLORS.gradient.gold,
                          color: COLORS.background.dark,
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          mb: 1.5,
                        }}
                      />

                      {/* Teams */}
                      <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: COLORS.text.primary,
                            fontWeight: '500',
                            fontSize: '0.875rem',
                            mb: 0.5,
                          }}
                        >
                          {homeTeam?.team_name || 'TBD'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: COLORS.text.secondary,
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          vs
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: COLORS.text.primary,
                            fontWeight: '500',
                            fontSize: '0.875rem',
                          }}
                        >
                          {awayTeam?.team_name || 'TBD'}
                        </Typography>
                      </Box>

                      {/* Match Details */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: COLORS.text.secondary,
                            fontSize: '0.75rem',
                          }}
                        >
                          {formatMatchTime(match.time)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StadiumIcon sx={{ fontSize: 14, color: COLORS.text.accent }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: COLORS.text.accent,
                              fontSize: '0.75rem',
                              fontWeight: '500',
                            }}
                          >
                            Field {match.field}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </StyledMatchCard>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default UpcomingMatches;
